import { AutocompleteProvider } from "../../types/enums";
import { LatLng } from "../maps/map-provider.interface";
import {
    AddressSuggestOptions,
    AddressSuggestion,
    GeoAddress,
    IAddressSuggestProvider,
    IGeocodingProvider
} from "./address-search-provider.interface";
import { GeoSearchError, isAbortError } from "./geo-search-fetch";
import { GeoSearchProviderHealth } from "./geo-search-provider-health";

export type GeoSearchOutcome = "answered" | "empty" | "failed";

export type GeoSearchChainOptions = {
    /**
     * Whether a provider answering with no results hands over to the next one. Worth it for a
     * one-shot geocode; on suggest it would bill a second vendor on every typo, so it stays off
     * until a provider with known data gaps (Adora's street file) joins the chain.
     */
    failoverOnEmptySuggest?: boolean,
    onProviderUsed?: (providerId: AutocompleteProvider, outcome: GeoSearchOutcome) => void
};

type ChainMember = { readonly providerId: AutocompleteProvider };

/**
 * Runs the Setting 275 primary and, when it fails, its failover. Providers are data services —
 * the chain never touches the DOM, so the same instance serves the suggest box and the
 * geocoding call sites.
 */
export class GeoSearchChain {
    private readonly _suggestProviders: IAddressSuggestProvider[];
    private readonly _geocodingProviders: IGeocodingProvider[];
    private readonly _options: GeoSearchChainOptions;
    private readonly _health = new GeoSearchProviderHealth();

    constructor(
        suggestProviders: IAddressSuggestProvider[],
        geocodingProviders: IGeocodingProvider[],
        options: GeoSearchChainOptions = {}
    ) {
        this._suggestProviders = suggestProviders;
        this._geocodingProviders = geocodingProviders;
        this._options = options;
    }

    public get hasSuggestProvider(): boolean {
        return this._suggestProviders.length > 0;
    }

    public get hasGeocodingProvider(): boolean {
        return this._geocodingProviders.length > 0;
    }

    public async suggestAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        const suggestions = await this._runAsync(
            this._suggestProviders,
            provider => provider.suggestAsync(query, options, signal),
            results => results.length === 0,
            this._options.failoverOnEmptySuggest ?? false
        );

        return suggestions ?? [];
    }

    public async resolveAsync(suggestion: AddressSuggestion): Promise<GeoAddress | null> {
        if (suggestion.address)
            return suggestion.address;

        const provider = this._suggestProviders.find(p => p.providerId === suggestion.providerId);
        if (!provider)
            return null;

        // Resolving belongs to the provider that produced the suggestion, so there is no
        // failover here — another vendor cannot make sense of this one's reference.
        try {
            return await provider.resolveAsync(suggestion);
        }
        catch (error) {
            if (isAbortError(error))
                throw error;

            this._recordFailure(provider.providerId, error);
            return null;
        }
    }

    public async geocodeAsync(query: string): Promise<LatLng | null> {
        return await this._runAsync(
            this._geocodingProviders,
            provider => provider.geocodeAsync(query),
            coords => coords === null,
            true
        );
    }

    public async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        return await this._runAsync(
            this._geocodingProviders,
            provider => provider.reverseGeocodeAsync(coords),
            address => address === null,
            true
        );
    }

    private async _runAsync<TProvider extends ChainMember, TResult>(
        providers: TProvider[],
        runAsync: (provider: TProvider) => Promise<TResult>,
        isEmpty: (result: TResult) => boolean,
        failoverOnEmpty: boolean
    ): Promise<TResult | null> {
        let lastError: unknown = null;

        for (const provider of this._orderByHealth(providers)) {
            try {
                const result = await runAsync(provider);
                const isEmptyResult = isEmpty(result);

                this._health.recordSuccess(provider.providerId);
                this._options.onProviderUsed?.(provider.providerId, isEmptyResult ? "empty" : "answered");

                if (isEmptyResult && failoverOnEmpty)
                    continue;

                return result;
            }
            catch (error) {
                // The caller cancelled (a newer keystroke); nobody failed.
                if (isAbortError(error))
                    throw error;

                this._recordFailure(provider.providerId, error);
                lastError = error;
            }
        }

        if (lastError)
            throw lastError;

        return null;
    }

    /**
     * Healthy providers first, parked ones after, so a parked primary is still tried when it is
     * the only thing left rather than leaving the caller with nothing.
     */
    private _orderByHealth<TProvider extends ChainMember>(providers: TProvider[]): TProvider[] {
        const healthy = providers.filter(p => !this._health.isParked(p.providerId));
        const parked = providers.filter(p => this._health.isParked(p.providerId));

        return [...healthy, ...parked];
    }

    private _recordFailure(providerId: AutocompleteProvider, error: unknown): void {
        if (!(error instanceof GeoSearchError) || error.isProviderUnhealthy)
            this._health.recordFailure(providerId);

        this._options.onProviderUsed?.(providerId, "failed");
    }
}
